import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import RecordWizard from '../../../reusable/formRecords/RecordWizards';
import { Field as FormField, Select as FormSelect, SectionHead, SectionDivider, ReadonlyBox, INP, INP_LOCKED } from '../../../reusable/formRecords/formFieldPrimitives';
import { MultiToggle as TogglePills } from '../../../shared/forms/FormControls';
import { FilterBar, SearchRow } from '../../../MarketTrad/shared';
import NlimsDataTable from '../../../shared/NlimsDataTable/NlimsDataTable';
import { useCountUp } from '../../../../hooks/useCountUp';
import { useAuth } from '../../../../context/AuthContext';
import { buildRoutePayload, conflictsApi } from '../../../../api/conflictsApi';
import { speciesApi } from '../../../../api/speciesLivestockRegistryApi';
import { useLocationOptions } from '../../../Admin/shared/useLocationOptions';
import { useCollectionSync } from '../../../../hooks/useCollectionSync';
import { ActionDropdown, anchorRect, RejectNotesModal, DeleteConfirmModal, actionCellHtml, WORKFLOW_RULES } from '../../../shared/RowActionMenu';
import GpsWaypointsField from '../../../shared/GpsWaypointsField';
import { normalizeGpsWaypoints } from '../../../../services/gpsCoordinates';
import { useRegistryLabel } from '../../../Admin/FoundationalTables/FormRegistry/registryFieldOverrides';

const ENCROACHMENT_OPTIONS = [
  { value: 'none', label: 'None' },
  { value: 'minor', label: 'Minor' },
  { value: 'moderate', label: 'Moderate' },
  { value: 'severe', label: 'Severe' },
];

const FORMALISATION_OPTIONS = [
  { value: 'formalised', label: 'Formalised' },
  { value: 'informal', label: 'Informal' },
];

const SEASONAL_USE_OPTIONS = [
  { value: 'dry_only', label: 'Dry Only' },
  { value: 'wet_only', label: 'Wet Only' },
  { value: 'year_round', label: 'Year Round' },
  { value: 'dry_primary', label: 'Dry Primary' },
];

const VIEW_STATUS_DOT = {
  draft: 'bg-gray-400',
  submitted: 'bg-blue-500',
  validated: 'bg-amber-500',
  approved: 'bg-green-500',
  rejected: 'bg-red-500',
};

const VIEW_STATUS_PILL = {
  draft: 'bg-gray-100 text-gray-600',
  submitted: 'bg-blue-50 text-blue-700',
  validated: 'bg-amber-50 text-amber-700',
  approved: 'bg-green-50 text-green-700',
  rejected: 'bg-red-50 text-red-600',
};

const BADGE_STYLE = {
  formalised: 'background:#dcfce7;color:#15803d;border:1px solid #86efac;',
  informal: 'background:#f3f4f6;color:#6b7280;border:1px solid #e5e7eb;',
  under_negotiation: 'background:#fef3c7;color:#b45309;border:1px solid #fcd34d;',
  none: 'background:#f3f4f6;color:#6b7280;border:1px solid #e5e7eb;',
  minor: 'background:#fef3c7;color:#b45309;border:1px solid #fcd34d;',
  moderate: 'background:#fed7aa;color:#c2410c;border:1px solid #fb923c;',
  severe: 'background:#fee2e2;color:#dc2626;border:1px solid #fca5a5;',
  yes: 'background:#dbeafe;color:#1d4ed8;border:1px solid #93c5fd;',
  no: 'background:#f3f4f6;color:#6b7280;border:1px solid #e5e7eb;',
};

function humanize(value) {
  return String(value ?? '')
    .trim()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function displayOrDash(value) {
  if (Array.isArray(value)) {
    return value.length ? value.join(', ') : '—';
  }
  if (value === null || value === undefined || value === '') return '—';
  return String(value);
}

function badgeHtml(data, type) {
  if (type !== 'display') return data ?? '';
  const raw = String(data ?? '').trim().toLowerCase();
  const style = BADGE_STYLE[raw] ?? 'background:#f3f4f6;color:#6b7280;border:1px solid #e5e7eb;';
  const label = humanize(data) || '—';
  return `<span style="display:inline-flex;padding:2px 10px;border-radius:6px;font-size:11px;font-weight:600;${style}">${label}</span>`;
}

function splitList(value) {
  if (Array.isArray(value)) return value.filter(Boolean).map((item) => String(item).trim()).filter(Boolean);
  return String(value ?? '')
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);
}

function Field({ label, children, span2 = false, required = false, hint }) {
  return (
    <div className={span2 ? 'col-span-2' : ''}>
      <FormField label={label} required={required} hint={hint}>
        {children}
      </FormField>
    </div>
  );
}

function Select({ label, value, onChange, options, span2 = false, required = false, readOnly = false, placeholder = 'Select...', hint }) {
  return (
    <Field label={label} span2={span2} required={required} hint={hint}>
      {readOnly ? (
        <ReadonlyBox>{value || 'Not set'}</ReadonlyBox>
      ) : (
        <FormSelect
          value={value ?? ''}
          onChange={onChange}
          options={options}
          placeholder={placeholder}
          className={INP}
        />
      )}
    </Field>
  );
}

function TextInput({ label, value, onChange, type = 'text', span2 = false, readOnly = false, placeholder = '', required = false, hint }) {
  return (
    <Field label={label} span2={span2} required={required} hint={hint}>
      <input
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        readOnly={readOnly}
        className={readOnly ? INP_LOCKED : INP}
      />
    </Field>
  );
}

function ReviewRow({ label: pageLabel, value }) {
  const label = useRegistryLabel(pageLabel);
  return (
    <div className="flex items-start justify-between gap-4 py-2 border-b border-gray-100 last:border-0">
      <span className="text-xs font-semibold text-gray-500">{label}</span>
      <span className="text-sm text-right text-gray-900">{value || value === 0 ? String(value) : '—'}</span>
    </div>
  );
}

// Review row for the captured route: shows the count, and expands into the
// list of waypoints in the order they were plotted (first = start of the
// route, last = end). Waypoints are stored as coordinates only.
function WaypointsReviewRow({ value }) {
  const [open, setOpen] = useState(false);
  const label = useRegistryLabel('GPS Waypoints');
  const points = normalizeGpsWaypoints(value);
  const pointName = (index) => {
    if (index === 0) return `Point ${index + 1} (Start)`;
    if (index === points.length - 1) return `Point ${index + 1} (End)`;
    return `Point ${index + 1}`;
  };

  if (points.length === 0) return <ReviewRow label="GPS Waypoints" value="" />;

  return (
    <div className="py-2 border-b border-gray-100 last:border-0">
      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        aria-expanded={open}
        className="flex w-full items-start justify-between gap-4 text-left"
      >
        <span className="text-xs font-semibold text-gray-500">{label}</span>
        <span className="flex items-center gap-1.5 text-sm text-gray-900">
          {points.length} waypoint{points.length === 1 ? '' : 's'} captured
          <svg className={`h-4 w-4 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </span>
      </button>
      {open && (
        <ol className="mt-2 space-y-1.5 rounded-xl border border-gray-100 bg-gray-50 p-2.5">
          {points.map((point, index) => (
            <li key={index} className="flex items-center gap-2 text-[12px]">
              <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-green-600 text-[10px] font-bold text-white">{index + 1}</span>
              <span className="font-semibold text-gray-700">{pointName(index)}</span>
              <span className="ml-auto tabular-nums text-gray-500">{point.lat.toFixed(5)}, {point.lng.toFixed(5)}</span>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

function StatCard({ end, isPercent = false, suffix, label, accent, accentColor = 'green' }) {
  const { count, ref } = useCountUp(end, 1800);
  const displayed = isPercent ? `${count}%` : count.toLocaleString('en-NG');

  return (
    <div ref={ref} className="bg-gray-50 rounded-xl p-5 flex-1 min-w-0">
      <p className="text-[26px] font-extrabold text-gray-900 leading-tight tabular-nums">
        {displayed}
        {suffix && <span className="text-[26px] font-extrabold text-gray-900"> {suffix}</span>}
      </p>
      <p className="text-[12px] text-gray-400 mt-1 leading-snug">{label}</p>
      <p className={`text-[11px] font-medium mt-1 leading-snug ${accentColor === 'green' ? 'text-green-500' : 'text-gray-400'}`}>
        {accent}
      </p>
    </div>
  );
}

export function RouteEditorModal({ mode, row, onClose, onSaved, speciesOptions, speciesLabelById, stateOptions, isFieldUser, modulePanel = null, onEdit, canEdit = true }) {
  const [form, setForm] = useState({
    routeName: '',
    originState: '',
    destinationState: '',
    statesTraversed: [],
    routeLengthKm: '',
    agreementStatus: 'formalised',
    seasonalUse: 'year_round',
    encroachmentStatus: 'none',
    primarySpecies: [],
    gpsWaypoints: null,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [step, setStep] = useState(0);
  const isReviewStep = step === 1;
  const currentDataStatus = (row?.data_status ?? row?.dataStatus ?? 'draft').toLowerCase();
  const routeLengthKm = Number(form.routeLengthKm);
  const canGoNext =
    form.routeName.trim().length >= 5 &&
    Boolean(form.originState) &&
    Boolean(form.destinationState) &&
    Number.isFinite(routeLengthKm) &&
    routeLengthKm >= 0.1 &&
    Boolean(form.agreementStatus) &&
    Boolean(form.seasonalUse) &&
    Boolean(form.encroachmentStatus) &&
    Array.isArray(form.statesTraversed) &&
    form.statesTraversed.length > 0 &&
    Array.isArray(form.primarySpecies) &&
    form.primarySpecies.length > 0;

  useEffect(() => {
    if (row) {
      setForm({
        routeName: row.routeName ?? row.route_name ?? '',
        originState: row.origin_state ?? row.originState ?? row.origin ?? '',
        destinationState: row.destination_state ?? row.destinationState ?? row.destination ?? '',
        statesTraversed: splitList(row.statesTraversed ?? row.states_traversed ?? []),
        routeLengthKm: String(row.route_length_km ?? row.routeLengthKm ?? row.route_width_metres ?? row.routeWidthMetres ?? ''),
        agreementStatus: row.agreement_status ?? row.agreementStatus ?? row.formalisation_status ?? row.formalisationStatus ?? 'formalised',
        seasonalUse: row.seasonal_use ?? row.seasonalUse ?? 'year_round',
        encroachmentStatus: row.encroachment_status ?? row.encroachmentStatus ?? 'none',
        primarySpecies: Array.isArray(row.primary_species ?? row.primarySpecies) ? (row.primary_species ?? row.primarySpecies) : splitList(row.primary_species ?? row.primarySpecies ?? []),
        gpsWaypoints: Array.isArray(row.waypoints) && row.waypoints.length >= 2
          ? { type: 'LineString', coordinates: row.waypoints }
          : null,
      });
      return;
    }

    setForm({
      routeName: '',
      originState: '',
      destinationState: '',
      statesTraversed: [],
      routeLengthKm: '',
      agreementStatus: 'formalised',
      seasonalUse: 'year_round',
      encroachmentStatus: 'none',
      primarySpecies: [],
      gpsWaypoints: null,
    });
  }, [row, mode]);

  const setValue = useCallback((key) => (event) => {
    const value = event?.target ? event.target.value : event;
    setForm((prev) => ({ ...prev, [key]: value }));
  }, []);

  const setStatesTraversed = (values) => {
    setForm((prev) => ({ ...prev, statesTraversed: values }));
  };

  const setSpecies = (values) => {
    setForm((prev) => ({ ...prev, primarySpecies: values }));
  };

  // Species are stored as registry uuids — show their names.
  const speciesLabels = Array.isArray(form.primarySpecies)
    ? form.primarySpecies.map((id) => speciesLabelById?.[id] ?? id).join(', ')
    : form.primarySpecies;

  if (mode === 'view') {
    const dotCls = VIEW_STATUS_DOT[currentDataStatus] ?? VIEW_STATUS_DOT.draft;
    const pillCls = VIEW_STATUS_PILL[currentDataStatus] ?? VIEW_STATUS_PILL.draft;
    return (
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 max-w-4xl mx-auto">
        <div className="flex items-start justify-between mb-6 gap-4">
          <div>
            <h2 className="text-base font-bold text-gray-900 uppercase tracking-wide">Record Details</h2>
            <p className="text-sm text-gray-400 mt-1">Review the information for this record</p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <span className="text-xs text-gray-500">Status:</span>
            <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold capitalize ${pillCls}`}>
              <span className={`h-1.5 w-1.5 rounded-full ${dotCls}`} />
              {currentDataStatus}
            </span>
          </div>
        </div>

        <div className="space-y-4">
          <SectionHead title="Route Registration" />
          <ReviewRow label="Route Name" value={form.routeName} />
          <ReviewRow label="Route Length (km)" value={form.routeLengthKm} />
          <ReviewRow label="Agreement Status" value={form.agreementStatus} />
          <ReviewRow label="Seasonal Use" value={form.seasonalUse} />
          <ReviewRow label="Encroachment Status" value={form.encroachmentStatus} />
          <WaypointsReviewRow value={form.gpsWaypoints} />

          <SectionHead title="Geospatial" />
          <ReviewRow label="Origin State" value={form.originState} />
          <ReviewRow label="Destination State" value={form.destinationState} />
          <ReviewRow label="States Traversed" value={Array.isArray(form.statesTraversed) ? form.statesTraversed.join(', ') : form.statesTraversed} />
          <ReviewRow label="Primary Species" value={speciesLabels} />

          <SectionHead title="System" />
          <ReviewRow label="Data Status" value={currentDataStatus} />
        </div>

        <div className="flex gap-3 pt-6 mt-6 border-t border-gray-100">
          <button type="button" onClick={onClose} className="flex-1 h-11 rounded-full border border-gray-200 bg-white text-sm font-semibold text-gray-600 hover:bg-gray-50">
            Back
          </button>
          {canEdit && (
            <button type="button" onClick={onEdit} className="flex-1 h-11 rounded-full bg-green-600 text-sm font-semibold text-white hover:bg-green-700">
              Edit Record
            </button>
          )}
        </div>
      </div>
    );
  }

  const submit = async (opts = {}) => {
    if (saving) return;
    if (form.routeName.trim().length < 5 || !form.originState || !form.destinationState || !form.routeLengthKm || !form.agreementStatus || !form.seasonalUse || !form.encroachmentStatus || !form.statesTraversed.length || !form.primarySpecies.length) {
      setError('Route name (min 5 chars), origin/destination states, route length, states traversed and primary species are required.');
      return;
    }
    const routeLengthKm = Number(form.routeLengthKm);
    if (!Number.isFinite(routeLengthKm) || routeLengthKm < 0.1) {
      setError('Route Length must be at least 0.1 km.');
      return;
    }

    const waypoints = form.gpsWaypoints?.coordinates ?? [];
    const payload = buildRoutePayload({
      routeName: form.routeName,
      originState: form.originState,
      destinationState: form.destinationState,
      statesTraversed: form.statesTraversed,
      routeLengthKm: form.routeLengthKm,
      agreementStatus: form.agreementStatus,
      seasonalUse: form.seasonalUse,
      encroachmentStatus: form.encroachmentStatus,
      primarySpecies: form.primarySpecies,
      waypoints,
      gps_waypoints: waypoints,
    });

    setSaving(true);
    setError('');
    try {
      if (mode === 'create') {
        await conflictsApi.routes.create(payload);
      } else if (row?.uuid) {
        await conflictsApi.routes.update(row.uuid, payload);
        if (opts.submitAfterSave) {
          await conflictsApi.routes.submit(row.uuid);
        }
      }
      toast.success(
        mode === 'create'
          ? 'Route created.'
          : (opts.submitAfterSave ? 'Route submitted for review.' : 'Route updated.'),
        { position: 'top-right', autoClose: 3000 }
      );
      await onSaved?.();
      onClose();
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Unable to save route.';
      setError(msg);
      toast.error(msg, { position: 'top-right', autoClose: 5000 });
    } finally {
      setSaving(false);
    }
  };

  // Same full-width pill buttons as the other Conflicts & Disputes wizards;
  // RecordWizard supplies the surrounding row and divider.
  const footer = (
    <>
      <button type="button" onClick={() => setStep((current) => Math.max(0, current - 1))} className="h-11 flex-1 rounded-full border border-gray-200 bg-white text-sm font-semibold text-gray-600 hover:bg-gray-50">
        Previous
      </button>
      {mode === 'edit' && isFieldUser ? (
        <>
          <button
            type="button"
            onClick={() => submit()}
            disabled={saving}
            className="h-11 flex-1 rounded-full border border-green-300 bg-white text-sm font-semibold text-green-700 hover:bg-green-50 disabled:opacity-60"
          >
            {saving ? 'Saving...' : (currentDataStatus === 'draft' ? 'Save as Draft' : 'Save Changes')}
          </button>
          <button
            type="button"
            onClick={() => submit({ submitAfterSave: true })}
            disabled={saving}
            className="h-11 flex-1 rounded-full bg-blue-600 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
          >
            {saving ? 'Submitting...' : 'Submit for Review'}
          </button>
        </>
      ) : (
        <button type="button" onClick={() => submit()} disabled={saving} className="h-11 flex-1 rounded-full bg-green-600 text-sm font-semibold text-white hover:bg-green-700 disabled:opacity-60">
          {saving ? 'Saving...' : (mode === 'create' ? 'Create Route' : 'Save Changes')}
        </button>
      )}
    </>
  );

  return (
    <RecordWizard
      steps={[
        { key: 'details', label: 'Route Registration' },
        { key: 'review', label: 'Review' },
      ]}
      currentStep={step}
      title={mode === 'create' ? 'Add New Record' : 'Edit Record'}
      subtitle={mode === 'create' ? 'Fill in the details to create a new record' : 'Update the details for this record'}
      statusLabel={currentDataStatus}
      onCancel={onClose}
      onBack={() => setStep((current) => Math.max(0, current - 1))}
      onNext={() => setStep(1)}
      canGoNext={canGoNext}
      nextLabel="Review"
      footer={isReviewStep ? footer : undefined}
      error={error}
    >
      {!isReviewStep && modulePanel}
      {isReviewStep ? (
        <>
          <SectionHead title="Route Registration" />
          <ReviewRow label="Route Name" value={form.routeName} />
          <ReviewRow label="Route Length (km)" value={form.routeLengthKm} />
          <ReviewRow label="Agreement Status" value={form.agreementStatus} />
          <ReviewRow label="Seasonal Use" value={form.seasonalUse} />
          <ReviewRow label="Encroachment Status" value={form.encroachmentStatus} />
          <WaypointsReviewRow value={form.gpsWaypoints} />

          <SectionHead title="Geospatial" />
          <ReviewRow label="Origin State" value={form.originState} />
          <ReviewRow label="Destination State" value={form.destinationState} />
          <ReviewRow label="States Traversed" value={Array.isArray(form.statesTraversed) ? form.statesTraversed.join(', ') : form.statesTraversed} />
          <ReviewRow label="Primary Species" value={speciesLabels} />

          <SectionHead title="System" />
          <ReviewRow label="Data Status" value="draft system-set" />
        </>
      ) : (
        <div className="space-y-5">

          <section className="space-y-3">
            <SectionHead title="Route Registration" />
            <div className="grid grid-cols-2 gap-4">
              <TextInput label="Route Name" required value={form.routeName} onChange={setValue('routeName')} placeholder="Min 5 characters" />
              <TextInput label="Route Length (km)" required type="number" value={form.routeLengthKm} onChange={setValue('routeLengthKm')} hint="≥0.1 km" />
              <Select label="Agreement Status" required value={form.agreementStatus} onChange={setValue('agreementStatus')} options={FORMALISATION_OPTIONS} />
              <Select label="Seasonal Use" required value={form.seasonalUse} onChange={setValue('seasonalUse')} options={SEASONAL_USE_OPTIONS} />
              <Select label="Encroachment Status" required value={form.encroachmentStatus} onChange={setValue('encroachmentStatus')} options={ENCROACHMENT_OPTIONS} />
              <div className="col-span-2">
                <GpsWaypointsField
                  label="GPS Waypoints"
                  value={form.gpsWaypoints}
                  onChange={(lineString) => setForm((prev) => ({ ...prev, gpsWaypoints: lineString }))}
                  hint="Click the map to plot the route — feeds the map view"
                />
              </div>
            </div>
          </section>

          <SectionDivider />

          <section className="space-y-3">
            <SectionHead title="Geospatial" />
            <div className="grid grid-cols-2 gap-4">
              <Select
                label="Origin State"
                required
                value={form.originState}
                onChange={setValue('originState')}
                options={stateOptions}
              />
              <Select
                label="Destination State"
                required
                value={form.destinationState}
                onChange={setValue('destinationState')}
                options={stateOptions}
              />
              <Field label="States Traversed" required span2 hint="Select all that apply">
                <TogglePills values={form.statesTraversed} onChange={setStatesTraversed} options={stateOptions} />
              </Field>
              <Field label="Primary Species" required span2 hint="Select all that apply">
                <TogglePills values={form.primarySpecies} onChange={setSpecies} options={speciesOptions} />
              </Field>
            </div>
          </section>

          <SectionDivider />

          <section className="space-y-3">
            <SectionHead title="System" />
            <div className="grid grid-cols-2 gap-4">
              <Field label="Data Status">
                <ReadonlyBox>draft system-set</ReadonlyBox>
              </Field>
            </div>
          </section>
        </div>
      )}
    </RecordWizard>
  );
}

export default function TranshumanceRoutesTab() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const roleId = user?.roleId ?? '';
  const isFieldUser = roleId === 'R04' || roleId === 'R05';
  const canManageRoutes = !['R02', 'R03', 'R06'].includes(roleId);
  const [records, setRecords] = useState([]);
  const [species, setSpecies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({ encroachmentStatus: '', agreementStatus: '', primarySpecies: '' });
  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [actionMenu, setActionMenu] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [quickBusy, setQuickBusy] = useState(false);
  // Routes cross state lines, so the editor lists every state (the record
  // itself is still saved under the user's own state by the API scope).
  const { stateOptions } = useLocationOptions('', '', { unscoped: true });

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [routesResult, speciesResult] = await Promise.all([
        conflictsApi.routes.listAll({ per_page: 1000, page: 1 }),
        speciesApi.listAll({ per_page: 1000, page: 1 }),
      ]);
      setRecords(Array.isArray(routesResult?.items) ? routesResult.items : []);
      setSpecies(Array.isArray(speciesResult?.items) ? speciesResult.items : []);
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Unable to load transhumance routes.';
      setError(msg);
      toast.error(msg, { position: 'top-right', autoClose: 5000 });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useCollectionSync(conflictsApi.routes.collectionKey, loadData);

  const speciesOptions = useMemo(
    () => species.map((item) => ({ value: item.uuid, label: item.common_name_nigeria || item.species_code || item.uuid })),
    [species]
  );
  const speciesLabelById = useMemo(
    () => Object.fromEntries(speciesOptions.map((option) => [option.value, option.label])),
    [speciesOptions]
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return records.filter((row) => {
      const searchable = [
        row.routeName,
        row.originState,
        row.destinationState,
        row.statesTraversed,
        row.routeLengthKm,
        row.agreementStatus,
        row.seasonalUse,
        row.encroachmentStatus,
        row.primarySpecies,
        row.waypoints,
      ].filter(Boolean).join(' ').toLowerCase();

      const primarySpecies = row.primary_species ?? row.primarySpecies ?? [];
      return (
        (!q || searchable.includes(q)) &&
        (!filters.encroachmentStatus || String(row.encroachment_status ?? '').toLowerCase() === filters.encroachmentStatus) &&
        (!filters.agreementStatus || String(row.agreement_status ?? '').toLowerCase() === filters.agreementStatus) &&
        (!filters.primarySpecies || primarySpecies.includes(filters.primarySpecies))
      );
    });
  }, [records, filters, search]);

  const columns = useMemo(() => [
    { title: 'Route Name', data: 'routeName', sortable: true, defaultContent: '' },
    { title: 'Origin State', data: 'originState', sortable: true, defaultContent: '' },
    { title: 'Destination State', data: 'destinationState', sortable: true, defaultContent: '' },
    { title: 'States Traversed', data: 'statesTraversed', sortable: true, defaultContent: '' },
    { title: 'Route Length (km)', data: 'routeLengthKm', sortable: true, defaultContent: '' },
    { title: 'Agreement Status', data: 'agreementStatus', sortable: true, defaultContent: '', render: badgeHtml },
    { title: 'Seasonal Use', data: 'seasonalUse', sortable: true, defaultContent: '', render: badgeHtml },
    { title: 'Encroachment Status', data: 'encroachmentStatus', sortable: true, defaultContent: '', render: badgeHtml },
    {
      title: 'Primary Species',
      data: 'primarySpecies',
      sortable: true,
      defaultContent: '',
      render: (data, type, row) => {
        const labels = (row.primary_species ?? row.primarySpecies ?? []).map((id) => speciesLabelById[id] ?? id);
        return type === 'display' ? displayOrDash(labels) : labels.join(', ');
      },
    },
    {
      title: 'GPS Waypoints',
      data: 'waypoints',
      sortable: true,
      defaultContent: '',
      render: (data, type, row) => {
        const hasWaypoints = Array.isArray(row.waypoints) && row.waypoints.length > 0;
        if (type !== 'display') return hasWaypoints ? 'Yes' : 'No';
        const style = hasWaypoints ? BADGE_STYLE.yes : BADGE_STYLE.no;
        return `<span style="display:inline-flex;padding:2px 10px;border-radius:6px;font-size:11px;font-weight:600;${style}">${hasWaypoints ? 'Yes' : 'No'}</span>`;
      },
    },
    { title: 'Action', data: null, sortable: false, defaultContent: '', render: () => actionCellHtml() },
  ], [speciesLabelById]);

  const handleRowAction = useCallback((action, row, meta) => {
    const rect = anchorRect(meta?.button);
    if (action === 'menu' || action == null) setActionMenu({ row, rect });
  }, []);

  const runQuickAction = useCallback(async (row, method) => {
    setQuickBusy(true);
    try {
      await conflictsApi.routes[method](row.uuid);
      await loadData();
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || `Failed to ${method} record.`;
      toast.error(msg, { position: 'top-right', autoClose: 5000 });
    } finally {
      setQuickBusy(false);
    }
  }, [loadData]);

  const handleRejectConfirm = useCallback(async (notes) => {
    if (!rejectTarget) return;
    setQuickBusy(true);
    try {
      await conflictsApi.routes.reject(rejectTarget.uuid, notes);
      await loadData();
      setRejectTarget(null);
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Failed to reject record.';
      toast.error(msg, { position: 'top-right', autoClose: 5000 });
      throw err;
    } finally {
      setQuickBusy(false);
    }
  }, [rejectTarget, loadData]);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteTarget) return;
    setQuickBusy(true);
    try {
      await conflictsApi.routes.remove(deleteTarget.uuid);
      await loadData();
      setDeleteTarget(null);
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Failed to delete record.';
      toast.error(msg, { position: 'top-right', autoClose: 5000 });
    } finally {
      setQuickBusy(false);
    }
  }, [deleteTarget, loadData]);

  if (error && records.length === 0 && !loading) {
    return (
      <section className="rounded-xl border border-red-100 bg-red-50 px-6 py-10 text-sm text-red-700">
        {error}
      </section>
    );
  }

  const total = records.length;
  const formalisedPct = total > 0 ? Math.round((records.filter((item) => String(item.formalisation_status ?? '').toLowerCase() === 'formalised').length / total) * 100) : 0;
  const encroached = records.filter((item) => String(item.encroachment_status ?? '').toLowerCase() !== 'none').length;
  const gpsPct = total > 0 ? Math.round((records.filter((item) => Array.isArray(item.waypoints) && item.waypoints.length > 0).length / total) * 100) : 0;

  return (
    <div>
      <div className="flex gap-4 mb-6">
        <StatCard end={total} suffix="Routes" label="Total Registered Routes" accent="Live route registry" accentColor="green" />
        <StatCard end={formalisedPct} isPercent suffix="Formalised" label="Formalised Agreements" accent="Routes with formalised status" accentColor="green" />
        <StatCard end={encroached} suffix="Encroached" label="Routes Under Encroachment" accent="Live backend count" accentColor="gray" />
        <StatCard end={gpsPct} isPercent suffix="GPS" label="Routes with GPS Waypoints" accent="Spatial data populated" accentColor="green" />
      </div>

      <FilterBar
        filters={[
      { key: 'encroachmentStatus', label: 'Encroachment Status', options: ENCROACHMENT_OPTIONS, defaultLabel: 'All' },
          { key: 'agreementStatus', label: 'Agreement Status', options: FORMALISATION_OPTIONS, defaultLabel: 'All' },
          { key: 'primarySpecies', label: 'Primary Species', options: speciesOptions, defaultLabel: 'All' },
          { key: 'state', label: 'State', options: stateOptions, defaultLabel: 'All', disabled: true },
        ]}
        filterValues={filters}
        onFilterChange={(key, value) => setFilters((prev) => ({ ...prev, [key]: value }))}
        onApply={() => {}}
      />

      <SearchRow searchValue={search} onSearch={setSearch} onCreate={() => navigate('/conflicts-dispute/new?tab=routes')} canCreate={canManageRoutes} />

      {loading && records.length === 0 ? (
        <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50 px-4 py-8 text-sm text-gray-500">
          Loading transhumance routes...
        </div>
      ) : (
        <NlimsDataTable
          tableId="routes-table"
          columns={columns}
          data={filtered}
          serverSide={false}
          hideSearch
          pageLength={10}
          enableRowSelection
          onRowAction={handleRowAction}
        />
      )}

      {actionMenu && !quickBusy && (
        <ActionDropdown
          row={actionMenu.row}
          rect={actionMenu.rect}
          roleId={roleId}
          rules={WORKFLOW_RULES.transhumanceRoutes}
          statusField="dataStatus"
          onClose={() => setActionMenu(null)}
          onView={() => { navigate(`/conflicts-dispute/view/${actionMenu.row.uuid}?tab=routes`, { state: { row: actionMenu.row } }); setActionMenu(null); }}
          onEdit={() => { navigate(`/conflicts-dispute/edit/${actionMenu.row.uuid}?tab=routes`, { state: { row: actionMenu.row } }); setActionMenu(null); }}
          onSubmit={() => runQuickAction(actionMenu.row, 'submit')}
          onValidate={() => runQuickAction(actionMenu.row, 'validate')}
          onApprove={() => runQuickAction(actionMenu.row, 'approve')}
          onReject={() => { setRejectTarget(actionMenu.row); setActionMenu(null); }}
          onDelete={() => { setDeleteTarget(actionMenu.row); setActionMenu(null); }}
        />
      )}

      {rejectTarget && (
        <RejectNotesModal
          onConfirm={handleRejectConfirm}
          onClose={() => setRejectTarget(null)}
        />
      )}

      {deleteTarget && (
        <DeleteConfirmModal
          row={deleteTarget}
          entityLabel={(r) => r?.routeName || ''}
          onConfirm={handleDeleteConfirm}
          onClose={() => setDeleteTarget(null)}
          busy={quickBusy}
        />
      )}

      {createOpen && (
        <RouteEditorModal
          mode="create"
          row={null}
          onClose={() => setCreateOpen(false)}
          onSaved={loadData}
          speciesOptions={speciesOptions}
          speciesLabelById={speciesLabelById}
          stateOptions={stateOptions}
          isFieldUser={isFieldUser}
        />
      )}
    </div>
  );
}
